/**
 * Common shape of API error response used across the app.
 * Use getErrorMessage() instead of reading these fields manually.
 */
export type ApiErrorResponse = {
  message?: string;
  error?: { message?: string; errorMessage?: string };
  errors?: Array<{ message?: string }>;
};

/**
 * Extracts a user-facing message from an unknown error (e.g. API/axios errors).
 * Checks, in order: response.data.message, response.data.error.message,
 * response.data.errors[0].message, error.message. Maps "Network Error" to a
 * friendly message. Use this instead of ad-hoc err.response?.data?.error?.message patterns.
 *
 * @param error - Caught error (unknown)
 * @param fallback - Message to return when no message can be extracted (default: 'Something went wrong')
 * @returns User-facing error message string
 */
export function getErrorMessage(
  error: unknown,
  fallback: string = 'Something went wrong',
): string {
  if (error == null) return fallback;

  const msg =
    typeof (error as Error).message === 'string'
      ? (error as Error).message
      : undefined;
  if (msg === 'Network Error') return 'Please check your internet connection';

  const response = (error as { response?: { data?: ApiErrorResponse } })
    ?.response;
  const data = response?.data;
  if (data) {
    if (typeof data.message === 'string' && data.message.trim())
      return data.message.trim();
    if (
      data.error &&
      typeof (data.error.message ?? data.error.errorMessage) === 'string'
    ) {
      const s = (data.error.message ?? data.error.errorMessage)?.trim();
      if (s) return s;
    }
    if (
      Array.isArray(data.errors) &&
      data.errors[0] &&
      typeof data.errors[0].message === 'string'
    ) {
      const s = data.errors[0].message.trim();
      if (s) return s;
    }
  }

  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  return fallback;
}

/**
 * Returns the HTTP status code from an API/axios error, if present.
 */
export function getErrorStatus(error: unknown): number | undefined {
  const response = (error as { response?: { status?: number } })?.response;
  return response?.status;
}
